import type { IssueView } from "@/types/lex";
import { pad } from "@/lib/format";
import { STATUS_DOT, STATUS_LABEL } from "./status";

interface IssueCardProps {
  issue: IssueView;
  onOpen: (day: number) => void;
  /** When true, the card has no own border (used inside a bordered grid). */
  flat?: boolean;
}

export function IssueCard({ issue, onOpen, flat = false }: IssueCardProps) {
  const locked = issue.status === "locked";
  const pct = Math.round((issue.progress / issue.total) * 100);
  // Suppress the amber "in-progress" styling for available-but-untouched days
  // so they read as neutral rather than urgent.
  const statusClass =
    issue.status === "in-progress" && issue.touched === 0
      ? ""
      : `status-${issue.status}`;

  return (
    <button
      className={`issue-card ${locked ? "is-locked" : ""} ${statusClass}`}
      style={
        flat
          ? { border: 0, borderRadius: 0, minHeight: 200, width: "100%", height: "100%" }
          : undefined
      }
      onClick={() => !locked && onOpen(issue.day)}
      disabled={locked}
    >
      <div className="issue-card__top">
        <div className="t-mono issue-card__num">DAY {pad(issue.day)}</div>
        <div className="row-c" style={{ gap: 6 }}>
          <span className={`dot dot--${STATUS_DOT[issue.status]}`} />
          <span className="t-meta" style={{ fontSize: 10 }}>{STATUS_LABEL[issue.status]}</span>
        </div>
      </div>
      <div className="issue-card__theme">
        <div
          className="t-display"
          style={{
            fontSize: "var(--t-xl)",
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            // Long single-word themes (ACCOMMODATIONS, TRANSPORTATION) need to
            // break gracefully on narrow columns instead of being clipped by
            // the card's `overflow: hidden`.
            overflowWrap: "anywhere",
            minWidth: 0,
          }}
        >
          {issue.theme_en}
        </div>
        <div
          className="t-display italic"
          style={{ fontSize: "var(--t-sm)", color: "var(--text-muted)", marginTop: 6 }}
        >
          {issue.theme_vi}
        </div>
      </div>
      <div className="issue-card__foot">
        <div className="issue-progress">
          <div className="issue-progress__bar" style={{ width: `${pct}%` }} />
        </div>
        <div className="row-s t-mono" style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
          <span>{pad(issue.progress)} / {issue.total}</span>
          <span>{pct}%</span>
        </div>
      </div>
    </button>
  );
}
